import DeviceManager, { onPromiseAttrType } from '../utils/device-manager';
import ScrollManager from '../utils/scroll-manager';
import FocusManager from '../utils/focus-manager';

export default function initHeader() : void {
    new Header(document.querySelector('.js-header'));
}

/**
 * The header class
 * 
 * @param {HTMLElement}   $header          The header element
 * @param {HTMLElement}   $menuToggler     The menu toggler button
 * @param {HTMLElement[]} $submenuTogglers The submenu togglers buttons
 * 
 * @param {HeaderMobile}  headerMobile      The HeaderMobile instance
 */
class Header {
    $header: HTMLElement;
    $menuToggler: HTMLElement;
    $submenuTogglers: HTMLElement[];

    headerMobile = new HeaderMobile(this);

    constructor($header: HTMLElement) {
        this.$header = $header;
        this.$menuToggler = this.$header.querySelector('.js-menu-toggler');
        this.$submenuTogglers = Array.from(this.$header.querySelectorAll('.js-submenu-toggler'));

        // Submenus are hidden by default to prevent transitions effects
        this.$header.querySelectorAll<HTMLElement>('.submenu-container').forEach($el => {
            $el.style.display = '';
        });

        // Get the pre-header height
        const $preHeader = document.querySelector<HTMLElement>('.js-pre-header');
        let preHeaderHeight = $preHeader ? $preHeader.offsetHeight : 0;

        if ($preHeader) {
            document.addEventListener('close-pre-header', () => {
                // Set the pre-header height to 0 if closed
                preHeaderHeight = 0;
            });
            window.addEventListener('resize', () => {
                // Update the pre-header height on resize
                preHeaderHeight = $preHeader.offsetHeight;
            });
        }

        // Add a class on body when the header is sticky
        window.addEventListener('scroll', () => {
            if (window.pageYOffset > preHeaderHeight) {
                document.body.classList.add('header-is-sticky');
            } else if (window.pageYOffset <= 20) {
                document.body.classList.remove('header-is-sticky');
            }
        });

        DeviceManager.addCallback((attr: onPromiseAttrType) => {
            if (!this.headerMobile.initialized && !DeviceManager.isGreaterThanOrEqual(attr.current, 'lg')) {
                this.headerMobile.init();
            } else if (this.headerMobile.initialized && DeviceManager.isGreaterThanOrEqual(attr.current, 'lg')) {
                this.headerMobile.destroy();
            }
        });
    }
}

/**
 * The mobile header class
 * 
 * @param {Header}  parent                  The Header parent class
 * @param {boolean} initialized             If the mobile header is initialized
 * 
 * @param {number}  previousScrollPos       The scroll position before opening the mobile menu
 * @param {boolean} previousScrollPosSmooth If the scrollTo method must be smooth after opening/closing the mobile menu
 */
class HeaderMobile {
    parent: Header;
    initialized = false;

    previousScrollPos = 0;
    previousScrollPosSmooth = false;

    constructor(parent: Header) {
        this.parent = parent;
    }

    /**
     * Initialize the mobile header
     * 
     * @return {void}
     */
    init() : void {
        this.initialized = true;

        // Click on menu toggler
        this.parent.$menuToggler.addEventListener('click', this.menuTogglerClickHandler);

        // Click on submenu toggler
        this.parent.$submenuTogglers.forEach($submenuToggler => {
            $submenuToggler.addEventListener('click', this.submenuTogglerClickHandler);
        });

        // Press a key
        document.addEventListener('keydown', this.onKeyDownHandler);
    }

    /**
     * Destroy the mobile header
     * 
     * @return {void}
     */
    destroy() : void {
        this.initialized = false;

        // Remove click event on menu toggler
        this.parent.$menuToggler.removeEventListener('click', this.menuTogglerClickHandler);

        // Remove click event on submenu toggler
        this.parent.$submenuTogglers.forEach($submenuToggler => {
            $submenuToggler.removeEventListener('click', this.submenuTogglerClickHandler);
        });

        // Remove keydown event on document
        document.removeEventListener('keydown', this.onKeyDownHandler);

        // Close the menu
        this.closeMenu(false);
    }

    /**
     * Click handler for $menuToggler
     * 
     * @return {void}
     */
    menuTogglerClickHandler = () : void => {
        if (document.body.classList.contains('header-mobile-is-open')) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    /**
     * Click handler for submenu togglers
     * 
     * @param {MouseEvent} evt
     * 
     * @return {void}
     */
    submenuTogglerClickHandler = (evt: MouseEvent) : void => {
        const $submenuToggler = <HTMLElement>evt.target;

        const $menuItem = $submenuToggler.closest<HTMLElement>('.menu-item');

        if (!$menuItem.classList.contains('open')) {
            this.openSubmenu($menuItem);
        } else {
            this.closeSubmenu($menuItem);
        }
    }

    /**
     * On keydown event
     * 
     * @param {KeyboardEvent} evt
     * 
     * @return {void}
     */
    onKeyDownHandler = (evt: KeyboardEvent) : void => {
        if (evt.key !== 'Escape') {
            return;
        }

        const $openedSubmenu = this.parent.$header.querySelector<HTMLElement>('.menu-item.open');
        if ($openedSubmenu) {
            this.closeSubmenu($openedSubmenu);
        } else {
            this.closeMenu();
        }
    }

    /**
     * Open the menu
     * 
     * @return {void}
     */
    openMenu() : void {
        this.previousScrollPos = window.scrollY; // Save the scroll position
        this.previousScrollPosSmooth = !document.body.classList.contains('header-is-sticky'); // Smooth scroll only if the header is not sticky

        document.body.classList.add('header-mobile-is-open');

        // Lock the scroll and scroll to the $header
        ScrollManager
            .preventScroll(true)
            .scrollTo(this.parent.$header, this.previousScrollPosSmooth);

        // Trap the focus inside the $header
        FocusManager.trapFocus(this.parent.$header);
    }

    /**
     * Close the menu
     * 
     * @param {boolean} withScroll
     * 
     * @return {void}
     */
    closeMenu(withScroll = true) : void {
        document.body.classList.remove('header-mobile-is-open');

        // Unlock the scroll and scroll to the previous scroll position
        ScrollManager.preventScroll(false);

        if (withScroll) {
            ScrollManager.scrollTo(this.previousScrollPos, this.previousScrollPosSmooth);
        }
        
        // Untrap the focus
        FocusManager.untrapFocus();

        // Reset the scroll pos vars
        this.previousScrollPos = 0;
        this.previousScrollPosSmooth = false;

        // Close all submenus
        this.parent.$header.querySelectorAll('.menu-item.open').forEach($item => {
            $item.classList.remove('open');
        });
    }

    /**
     * Open a submenu
     * 
     * @param {HTMLElement} $menuItem The menu-item containing the submenu
     * 
     * @return {void}
     */
    openSubmenu($menuItem: HTMLElement) : void {
        $menuItem.classList.add('open');

        // Trap the focus inside the submenu and site-logo-container
        FocusManager.trapFocus([this.parent.$header.querySelector('.site-logo-container'), $menuItem.querySelector('.submenu-container')]);
    }

    /**
     * Close a submenu
     * 
     * @param {HTMLElement} $menuItem The menu-item containing the submenu
     * 
     * @return {void}
     */
    closeSubmenu($menuItem: HTMLElement) : void {
        $menuItem.classList.remove('open');

        // Trap the focus inside the $header
        FocusManager.trapFocus(this.parent.$header);
    }
}
